'use client';

import {
	OracleClient,
	OraclePriceData,
	OracleSource,
	PRICE_PRECISION,
	PublicKey,
	ZERO,
	getOracleClient,
} from '@velocity-exchange/sdk';
import { ENUM_UTILS, MarketId, UIMarket } from '@velocity-exchange/common';
import { useEffect, useMemo, useRef } from 'react';
import useDriftStore from 'src/stores/DriftStore/useDriftStore';
import { usePathname } from 'next/navigation';
import { needsDataRequirement } from 'src/utils/routeDataRequirements';
import useOraclePriceStore, {
	OraclePriceStore,
} from 'src/stores/useOraclePriceStore';
import NumLib from 'src/utils/NumLib';
import { useImmer } from 'use-immer';
import { MarketListeningSelection } from '../stores/useMarketStateStore';
import useDriftClientIsReady from './useDriftClientIsReady';
import useInterval from './useInterval';
import useMarketsForDlobToTrack from './useMarketsForDlobToTrack';
import { dlog } from 'src/dev';
import { usePerpMarketsLookup } from './perpMarkets/usePerpMarketsLookup';
import { useSpotMarketsLookup } from './spotMarkets/useSpotMarketsLookup';

export type FormattedOraclePriceData = {
	price: number;
	slot: number;
	confidence: number;
	twap?: number;
	twapConfidence?: number;
	hasSufficientNumberOfDataPoints: OraclePriceData['hasSufficientNumberOfDataPoints'];
	maxPrice?: number;
};

const ORACLE_CLIENT_KEYS = Object.values(OracleSource).map(
	(value) => Object.keys(value)[0]
);
type OracleClientsMap = Map<string, OracleClient | undefined>;

const useSyncOracleDataWithRpc = () => {
	const pathname = usePathname();
	const shouldSync = needsDataRequirement(pathname ?? '', 'trading');
	const perpMarketsLookup = usePerpMarketsLookup();
	const spotMarketsLookup = useSpotMarketsLookup();
	const connection = useDriftStore((s) => s.connection);
	const bulkLoader = useDriftStore((s) => s.connection.accountLoader);
	const driftClient = useDriftStore((s) => s.driftClient.client);
	const driftClientIsReady = useDriftClientIsReady();

	const oracleClientMap = useRef<OracleClientsMap>(new Map());

	const marketsToTrackWithBlockchain = useMarketsForDlobToTrack(
		MarketListeningSelection.BLOCKCHAIN
	);

	const usdcMarket = MarketId.createSpotMarket(0);

	const marketsAndAccountsToUse: {
		market: UIMarket;
		accountToUse: PublicKey;
	}[] = useMemo(() => {
		if (perpMarketsLookup.length < 2 || spotMarketsLookup.length < 2) {
			// this is the market look ups initialized with only 1 length as a default, so this is returning an empty array before the app wrapper has loaded the remote configs
			return [];
		}
		return [usdcMarket, ...marketsToTrackWithBlockchain.allMarkets].map(
			(market) => {
				const isPerp = market.isPerp;

				return {
					market: isPerp
						? UIMarket.createPerpMarket(market.marketIndex)
						: UIMarket.createSpotMarket(market.marketIndex),
					accountToUse: isPerp
						? perpMarketsLookup[market.marketIndex]?.oracle
						: spotMarketsLookup[market.marketIndex]?.oracle,
				};
			}
		);
	}, [
		perpMarketsLookup,
		spotMarketsLookup,
		marketsToTrackWithBlockchain.categories,
	]);

	const set = useOraclePriceStore((s) => s.set);

	// Keep a local price store state so that the app isn't re-rendering non-stop for every price change
	const [localPriceStoreState, setLocalPriceStoreState] = useImmer<
		OraclePriceStore['symbolMap']
	>({});

	const bulkLoaderCallbacks = useRef<[string, PublicKey][]>([]);

	// Initialize the oracle clients
	useEffect(() => {
		if (!driftClient?.program || !connection.current) return;

		ORACLE_CLIENT_KEYS.forEach((oracleSourceKey) => {
			if (!connection.current) return;
			const oracleSource = ENUM_UTILS.toObj(oracleSourceKey);

			oracleClientMap.current.set(
				oracleSourceKey,
				getOracleClient(oracleSource, connection.current, driftClient.program)
			);
		});
	}, [connection.current, driftClient?.program]);

	const getClient = useMemo(
		() => (oracleSource: OracleSource) => {
			if (!driftClient) return undefined;

			const oracleSourceString = ENUM_UTILS.toStr(oracleSource);
			const oracleClient = oracleClientMap.current.get(oracleSourceString);

			if (!oracleClient) {
				throw new Error(`Oracle client not found for ${oracleSourceString}`);
			}

			return oracleClient;
		},
		[driftClient]
	);

	useEffect(() => {
		if (!connection) return;
		if (!driftClientIsReady) return;
		if (!driftClient) return;

		const updateOracleDataWithAccountBuffer = async (
			market: UIMarket,
			accountDataBuffer: Buffer
		) => {
			const accountClient = getClient(market.market.oracleSource);
			if (!accountDataBuffer) {
				return;
			}
			const oraclePriceData =
				accountClient?.getOraclePriceDataFromBuffer(accountDataBuffer);

			setLocalPriceStoreState((s) => {
				if (!oraclePriceData) return s;
				s[market.key] = {
					market,
					priceData: {
						price: NumLib.formatBn.toRawNum(
							oraclePriceData?.price ?? ZERO,
							PRICE_PRECISION
						),
						slot: oraclePriceData?.slot?.toNumber() ?? 0,
						confidence: NumLib.formatBn.toRawNum(
							oraclePriceData?.confidence ?? ZERO,
							PRICE_PRECISION
						),
						twap: NumLib.formatBn.toRawNum(
							oraclePriceData?.twap ?? ZERO,
							PRICE_PRECISION
						),
						twapConfidence: NumLib.formatBn.toRawNum(
							oraclePriceData?.twapConfidence ?? ZERO,
							PRICE_PRECISION
						),
						hasSufficientNumberOfDataPoints:
							oraclePriceData?.hasSufficientNumberOfDataPoints ?? false,
						maxPrice: NumLib.formatBn.toRawNum(
							oraclePriceData?.maxPrice ?? ZERO,
							PRICE_PRECISION
						),
					},
					rawPriceData: oraclePriceData,
				};
			});
		};

		dlog(
			`oracle_price_debugging`,
			`oracle_rpc_sync_markets_to_use`,
			marketsAndAccountsToUse.map(({ market }) => market.key).join(',')
		);

		// TODO: this over-renders and runs too often
		// Subsubscribe to account updates
		marketsAndAccountsToUse.forEach(({ market, accountToUse }) => {
			bulkLoader
				?.addAccount(accountToUse, async (accountDataBuffer) => {
					updateOracleDataWithAccountBuffer(market, accountDataBuffer);
				})
				.then((callbackId) => {
					bulkLoaderCallbacks.current.push([callbackId, accountToUse]);
				});
		});

		// Trigger a manual initial update for all markets
		marketsAndAccountsToUse.forEach(({ market, accountToUse }) => {
			const prefetchedResult = bulkLoader?.bufferAndSlotMap.get(
				accountToUse?.toString() ?? ''
			);

			if (!prefetchedResult || !prefetchedResult.buffer) return;

			updateOracleDataWithAccountBuffer(market, prefetchedResult.buffer);
		});

		return () => {
			// Clean up bulk loader account listeners
			if (bulkLoaderCallbacks.current.length) {
				bulkLoaderCallbacks.current.forEach(([callbackId, accountKey]) => {
					bulkLoader?.removeAccount(accountKey, callbackId);
				});
				bulkLoaderCallbacks.current = [];
			}
		};
	}, [connection, bulkLoader, driftClientIsReady, marketsAndAccountsToUse]);

	// Update the price store every 500ms
	useInterval(
		() => {
			if (!shouldSync) return;
			set((s) => {
				s.symbolMap = localPriceStoreState;
			});
		},
		shouldSync ? 1000 : Number.MAX_SAFE_INTEGER
	);
};

export default useSyncOracleDataWithRpc;
